import * as z from "zod";

/**
 * Lowercase slug identifying a partnership. It prefixes the partner's
 * amplitude events, so it never changes.
 */
export const SupportPartnerKeySchema = z
  .string()
  .regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    "Use lowercase letters, numbers and dashes",
  );

/** Merchants open these with `window.open` and `<img src>`, so only https. */
const HttpsUrlSchema = z.url({ protocol: /^https$/ });

const PartnerActionSchema = z.object({
  text: z.string().min(1),
  url: HttpsUrlSchema,
});

const SupportPartnershipContentSchema_ = z.object({
  primaryAction: PartnerActionSchema,
  secondaryAction: PartnerActionSchema,
  /** The surge-triggered toolbar button and the modal it opens. */
  button: z.object({
    text: z.string().min(1),
    modalTitle: z.string().min(1),
    /** Falls back to an image bundled for the partner key, if any. */
    imageUrl: HttpsUrlSchema.nullable(),
    intro: z.string().min(1),
    valueProps: z.array(z.string().min(1)),
  }),
  notificationDescription: z.string().min(1),
  /** Null keeps the default threshold-alert channel. */
  slackChannelId: z
    .string()
    .regex(/^C[A-Z0-9]{8,}$/, "Use a Slack channel ID, like C0123456789")
    .nullable()
    .default(null),
  /** The always-on card on the support analytics dashboard. */
  card: z.object({
    eyebrow: z.string().min(1),
    headline: z.string().min(1),
    primaryActionText: z.string().min(1),
  }),
});
export const SupportPartnershipContentSchema: z.ZodType<SupportPartnershipContent> =
  SupportPartnershipContentSchema_;
export interface SupportPartnershipContent extends z.infer<
  typeof SupportPartnershipContentSchema_
> {}

/** Merchant-facing copy. The Slack channel stays on the server. */
export const MerchantSupportPartnershipContentSchema =
  SupportPartnershipContentSchema_.omit({ slackChannelId: true });
export interface MerchantSupportPartnershipContent extends z.infer<
  typeof MerchantSupportPartnershipContentSchema
> {}

export const MAX_SUPPORT_PARTNERSHIP_WEIGHT = 1_000_000;

/** Weight 0 keeps a partnership registered but never shown. */
export const SupportPartnershipWeightSchema = z
  .number()
  .int()
  .min(0)
  .max(MAX_SUPPORT_PARTNERSHIP_WEIGHT);

const SupportPartnershipInputSchema_ = z.object({
  name: z.string().trim().min(1),
  weight: SupportPartnershipWeightSchema,
  content: SupportPartnershipContentSchema,
});
export const SupportPartnershipInputSchema: z.ZodType<SupportPartnershipInput> =
  SupportPartnershipInputSchema_;
export interface SupportPartnershipInput extends z.infer<
  typeof SupportPartnershipInputSchema_
> {}

const SupportPartnershipSchema_ = SupportPartnershipInputSchema_.extend({
  partnerKey: SupportPartnerKeySchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const SupportPartnershipSchema: z.ZodType<SupportPartnership> =
  SupportPartnershipSchema_;
export interface SupportPartnership extends z.infer<
  typeof SupportPartnershipSchema_
> {}

type WeightedPartnership = Pick<SupportPartnership, "partnerKey" | "weight">;

/** Each shown partnership's share of impressions, 0 to 1; weight 0 is left out. */
export function supportPartnershipShares(
  partnerships: readonly WeightedPartnership[],
): Map<string, number> {
  const total = partnerships.reduce((sum, { weight }) => sum + weight, 0);
  return new Map(
    partnerships
      .filter(({ weight }) => weight > 0)
      .map(({ partnerKey, weight }) => [partnerKey, weight / total]),
  );
}

/** Rotates the partnership a team sees once a day, at UTC midnight. */
export function supportPartnershipSeed(teamId: string, now: Date): string {
  return `${teamId}:${now.toISOString().slice(0, 10)}`;
}

/** Lowest `-ln(u) / weight` wins. A change only moves teams to or from that partner. */
export function pickSupportPartnership<T extends WeightedPartnership>(
  partnerships: readonly T[],
  seed: string,
): T | null {
  let best: { partnership: T; score: number } | null = null;
  for (const partnership of partnerships) {
    if (partnership.weight <= 0) {
      continue;
    }
    const score =
      -Math.log(hashToUnitInterval(`${seed}:${partnership.partnerKey}`)) /
      partnership.weight;
    if (
      !best ||
      score < best.score ||
      (score === best.score &&
        partnership.partnerKey < best.partnership.partnerKey)
    ) {
      best = { partnership, score };
    }
  }
  return best?.partnership ?? null;
}

type AlertPartnership = WeightedPartnership & {
  name: string;
  slackChannelId: string | null;
};

/** The channel for the partner a team sees today, else `defaultChannelId`. */
export function supportPartnershipAlertChannel(
  partnerships: readonly AlertPartnership[],
  teamId: string,
  now: Date,
  defaultChannelId: string,
): {
  channelId: string;
  partnerKey: string | null;
  partnerName: string | null;
} {
  const partner = pickSupportPartnership(
    partnerships,
    supportPartnershipSeed(teamId, now),
  );
  if (!partner) {
    return { channelId: defaultChannelId, partnerKey: null, partnerName: null };
  }
  return {
    channelId: partner.slackChannelId ?? defaultChannelId,
    partnerKey: partner.partnerKey,
    partnerName: partner.name,
  };
}

/** FNV-1a with a murmur3 finalizer, mapped to (0, 1). */
function hashToUnitInterval(seed: string): number {
  let hash = 0x81_1c_9d_c5;
  for (let i = 0; i < seed.length; i++) {
    hash = Math.imul(hash ^ seed.charCodeAt(i), 0x01_00_01_93);
  }
  hash = Math.imul(hash ^ (hash >>> 16), 0x85_eb_ca_6b);
  hash = Math.imul(hash ^ (hash >>> 13), 0xc2_b2_ae_35);
  hash ^= hash >>> 16;
  return ((hash >>> 0) + 0.5) / 0x1_00_00_00_00;
}
